import { useEffect, useState } from "react";
import { Smartphone, X, Share } from "lucide-react";

const DISMISS_KEY = "candle_pwa_dismissed";

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS doesn't fire beforeinstallprompt — show a manual hint after a delay
    if (isIOS()) {
      const t = setTimeout(() => {
        setIosHint(true);
        setShow(true);
      }, 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") {
      setShow(false);
    }
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-50 surface-card border-bull/40 bg-[#0a0a0a]/95 backdrop-blur-md p-4 shadow-2xl"
      data-testid="install-prompt"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-neutral-500 hover:text-white"
        aria-label="dismiss"
        data-testid="install-prompt-dismiss"
      >
        <X className="h-4 w-4" strokeWidth={1.5} />
      </button>
      <div className="flex items-start gap-3">
        <Smartphone className="h-5 w-5 text-bull shrink-0 mt-0.5" strokeWidth={1.5} />
        <div className="text-sm min-w-0 pr-6">
          <p className="font-medium text-white">Install CANDLE.AI</p>
          {iosHint ? (
            <p className="mt-1 text-xs text-neutral-400 leading-relaxed">
              Tap <Share className="inline h-3.5 w-3.5 mx-0.5" strokeWidth={1.5} /> then{" "}
              <span className="font-mono text-bull">Add to Home Screen</span> for a full-screen
              app-like experience.
            </p>
          ) : (
            <>
              <p className="mt-1 text-xs text-neutral-400 leading-relaxed">
                Install the app for a full-screen, offline-ready experience.
              </p>
              <button
                onClick={install}
                className="mt-3 inline-flex items-center px-3 h-8 bg-bull text-black font-medium font-mono text-xs hover:bg-bull/90"
                data-testid="install-prompt-cta"
              >
                Install →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
